import React, { useEffect, useRef } from 'react';
import { Modal } from 'antd';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { getProductPrice, getPromoPrice } from '../../../common/common';
import { getPlanFeatures, getPlanName } from '../../../helpers/planTranslations';
import { getMbpsFromPlan } from '../../../helpers/productLabels';
import { saveInternetPlan } from '../../../store/action/OrderAction';

const SPEED_GUIDE_TIERS = [100, 300, 500, 1000, 5000];

const getSpeedGuideCopy = (plan, t, fixedWirelessProducts, spanishLanguagePref) => {
  const mbps = getMbpsFromPlan(plan);
  if (SPEED_GUIDE_TIERS.includes(mbps)) {
    return {
      useCase: t(`singlePageCheckout.speedGuide${mbps}UseCase`),
      household: t(`singlePageCheckout.speedGuide${mbps}Household`),
    };
  }
  const features = getPlanFeatures({ plan, spanishLanguagePref, fixedWirelessProducts });
  return {
    useCase: features[0] || '',
    household: t('singlePageCheckout.speedGuideGenericHousehold'),
  };
};

const SpeedGuideModal = ({ plans, serviceTerm, spanishLanguagePref, selectedServRef, onClose, fixedWirelessProducts = [] }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const trackRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      const track = trackRef.current;
      const selected = track?.querySelector('.speed-guide-column.is-selected');
      if (track && selected) {
        track.scrollLeft = selected.offsetLeft - (track.clientWidth - selected.offsetWidth) / 2;
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [plans]);

  const handleSelect = (plan) => {
    // Same action the tier buttons and the auto-selected default use - selecting a plan
    // here behaves identically to selecting it anywhere else in the single-page checkout.
    dispatch(saveInternetPlan(plan));
    onClose();
  };

  return (
    <Modal
      visible
      onCancel={onClose}
      footer={null}
      width={1160}
      className="speed-guide-modal"
      title={t('singlePageCheckout.speedGuideTitle')}
    >
      <div className="speed-guide-slider">
        <div className="speed-guide-modal__columns" ref={trackRef}>
          {plans?.map((plan) => {
            const promoPrice = getPromoPrice(plan, serviceTerm);
            const price = promoPrice || getProductPrice(plan, serviceTerm);
            const isSelected = plan.servRef === selectedServRef;
            const copy = getSpeedGuideCopy(plan, t, fixedWirelessProducts, spanishLanguagePref);

            return (
              <div className={`speed-guide-column ${isSelected ? 'is-selected' : ''}`} key={plan.servRef}>
                <div className="speed-guide-column__header">
                  {getPlanName({ plan, spanishLanguagePref })}
                </div>
                <div className="speed-guide-column__body">
                  <div className="speed-guide-column__price">{price} {t('singlePageCheckout.perMonth')}</div>
                  <div className="speed-guide-column__use-case">{copy.useCase}</div>
                  <div className="speed-guide-column__household">{copy.household}</div>
                </div>
                <button
                  type="button"
                  className={`speed-guide-column__button ${isSelected ? 'is-selected' : ''}`}
                  onClick={() => handleSelect(plan)}
                >
                  {isSelected ? t('singlePageCheckout.selected') : t('singlePageCheckout.chooseThis')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </Modal>
  );
};

SpeedGuideModal.propTypes = {
  plans: PropTypes.array,
  serviceTerm: PropTypes.array,
  spanishLanguagePref: PropTypes.bool,
  selectedServRef: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  fixedWirelessProducts: PropTypes.array,
};

export default SpeedGuideModal;
